/**
 * CtextfxReversingDecrypt
 * A decrypt line that resolves outward from its origins, holds, then animates
 * the un-resolve back into them, so the loop reads as one continuous breath
 * rather than a hard reset.
 *
 * The locked loading treatment (tweak 603ixag), lifted out of
 * `SessionPanelLoadingTitle` so every "still loading" label breathes the same
 * way instead of each surface growing its own clock.
 */

"use client";

import { useEffect, useMemo, useState, type ReactElement } from "react";
import { useReducedMotion } from "framer-motion";
import { Ctextfx } from "./Ctextfx";
import { CtextfxVariant } from "./Ctextfx.constants";
import { pickDecryptOrigins } from "./Ctextfx.decrypt";

/** One breath: resolve, hold resolved, un-resolve, hold scrambled. Written as
 *  phase durations rather than a chars-per-second ramp because the un-resolve
 *  has to take exactly as long as the resolve for the loop to read symmetric. */
const RESOLVE_MS = 1500;
const HOLD_RESOLVED_MS = 900;
const HOLD_SCRAMBLED_MS = 400;
const CYCLE_MS = RESOLVE_MS * 2 + HOLD_RESOLVED_MS + HOLD_SCRAMBLED_MS;

/** 0 = fully scrambled, 1 = fully resolved, on the cycle above. */
function reversingRevealProgress(elapsedMs: number): number {
  const phase = elapsedMs % CYCLE_MS;
  if (phase < RESOLVE_MS) {
    return phase / RESOLVE_MS;
  }
  const afterResolve = phase - RESOLVE_MS;
  if (afterResolve < HOLD_RESOLVED_MS) {
    return 1;
  }
  const unresolved = afterResolve - HOLD_RESOLVED_MS;
  if (unresolved < RESOLVE_MS) {
    return 1 - unresolved / RESOLVE_MS;
  }
  return 0;
}

/** Position of each character in the resolve order, ranked by distance from the
 *  decrypt origins. Reversing the walk is then just counting the same ranks back
 *  down, so the un-resolve retraces the resolve exactly — outward from the
 *  origins, then collapsing back into them. One character per step rather than
 *  one decrypt radius per step: a radius only moves a handful of times across a
 *  short line, which over a seconds-long breath would read as jumps, not a walk. */
function reversingRevealRanks(input: {
  length: number;
  origins: readonly number[];
}): number[] {
  const { length, origins } = input;
  const indices = Array.from({ length }, (_, index) => index);
  const distances = indices.map((index) =>
    origins.reduce(
      (nearest, origin) => Math.min(nearest, Math.abs(index - origin)),
      length,
    ),
  );
  const ranks = indices.slice();
  const order = indices
    .slice()
    .sort(
      (left, right) =>
        (distances[left] ?? 0) - (distances[right] ?? 0) || left - right,
    );
  order.forEach((index, rank) => {
    ranks[index] = rank;
  });
  return ranks;
}

export interface CtextfxReversingDecryptProps {
  text: string;
  /**
   * Stable across cycles: Ctextfx keys its character row off this, and a
   * remount mid-breath would reshuffle every scramble glyph. It also seeds the
   * decrypt origins, so a different key re-jitters the line a surface was
   * locked with.
   */
  playKey: string;
}

/** Ctextfx's own radius ramp only ever counts up, so a reversing loop cannot be
 *  a replayed entrance — a replay snaps back to scramble. This drives Ctextfx
 *  from an external mask instead: one clock walks the mask out and back, and
 *  every character the mask takes away re-enters the scramble, so the un-resolve
 *  animates exactly the way the resolve did. */
export function CtextfxReversingDecrypt({
  text,
  playKey,
}: CtextfxReversingDecryptProps): ReactElement {
  const shouldReduceMotion = useReducedMotion() === true;
  const length = useMemo(() => Array.from(text).length, [text]);
  const origins = useMemo(
    () => pickDecryptOrigins({ length, playKey }),
    [length, playKey],
  );
  const ranks = useMemo(
    () => reversingRevealRanks({ length, origins }),
    [length, origins],
  );
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (shouldReduceMotion) {
      return;
    }
    let frame = 0;
    const start = performance.now();
    const tick = (now: number): void => {
      const nextStep = Math.round(reversingRevealProgress(now - start) * length);
      setStep((previous) => (previous === nextStep ? previous : nextStep));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [shouldReduceMotion, length]);

  const revealed = useMemo(
    () => ranks.map((rank) => rank < step),
    [ranks, step],
  );

  if (shouldReduceMotion) {
    return <>{text}</>;
  }

  return (
    <Ctextfx
      text={text}
      variant={CtextfxVariant.Decrypt}
      playKey={playKey}
      play
      scrambleUnresolved
      reserveWidth
      controlledRevealed={revealed}
    />
  );
}
